import React, { useMemo, useState, useEffect } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet, Platform, ActivityIndicator, ScrollView } from 'react-native';
import { IMaintenanceInfoResponse } from '@/store/api/maintenanceApi';
import { ADMIN_COLORS } from '@/constants/admin';
import { DesktopDropdownSelector, DesktopDropdownOption } from '@/components/common/admin/desktop/DesktopDropdownSelector';
import { FileUploadSection, UploadFile } from '@/components/common/admin/mobile/FileUploadSection';
import { RNButton } from '@/components/common/residents/desktop/Button';
import { useToast } from '@/contexts/admin/ToastContext';
import { useAppSelector } from '@/store';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { sortMaintenanceSeverityNames } from '@/utils/residents/maintenance/maintenance';
import { normalizePhoneInput } from '@/utils/common/regex';
import { useMaintenanceForm } from '@/hooks/residents/useMaintenanceForm';
import { useSubmitMaintenanceTicket } from '@/hooks/residents/useSubmitMaintenanceTicket';
import { MAX_ATTACHMENTS } from '@/constants/residents';

interface CreateMaintenanceTicketModalProps {
  visible: boolean;
  onClose: () => void;
}

export const CreateMaintenanceTicketModal: React.FC<CreateMaintenanceTicketModalProps> = ({ visible, onClose }) => {
  const { submit, status } = useSubmitMaintenanceTicket();
  const authUser = useAppSelector((s) => s.auth.user as any);
  const [files, setFiles] = useState<UploadFile[]>([]);
  const { showSuccess, showError } = useToast();

  const {
    description, setDescription,
    location, setLocation,
    category, setCategory,
    subCategory, setSubCategory,
    problem, setProblem,
    severity, setSeverity,
    phoneNumber, setPhoneNumber,
    permissionChoice, setPermissionChoice,
    locationTouched, setLocationTouched,
    problemTouched, setProblemTouched,
    categoryTouched, setCategoryTouched,
    subCategoryTouched, setSubCategoryTouched,
    severityTouched, setSeverityTouched,
    phoneTouched, setPhoneTouched,
    maintenanceInfo,
    locationOptions,
    categoryOptions,
    subCategoryOptions,
    problemOptions,
    severityOptions,
    isFormValid,
    phoneNumberRegex,
    loadInfo,
    isInfoError,
    infoErrorStatus,
  } = useMaintenanceForm();

  useEffect(() => { if (visible) loadInfo(); }, [visible, loadInfo]);
  useEffect(() => { if (visible && authUser?.phoneNumber) setPhoneNumber(String(authUser.phoneNumber)); }, [visible, authUser, setPhoneNumber]);

  // On info load error, show toast and close modal
  useEffect(() => {
    if (visible && isInfoError && (infoErrorStatus === 500 || infoErrorStatus == null)) {
      showError('Unable to load maintenance options. Please contact the front desk.');
      onClose();
    }
  }, [visible, isInfoError, infoErrorStatus, showError, onClose]);

  // File selection handled by FileUploadSection (web and native)
  const handleFilesChange = (newFiles: UploadFile[]) => {
    if (newFiles.length > MAX_ATTACHMENTS) {
      showError(`You can attach up to ${MAX_ATTACHMENTS} files`);
      setFiles(newFiles.slice(0, MAX_ATTACHMENTS));
    } else {
      setFiles(newFiles);
    }
  };

  const handleSubmit = async () => {
    try {
      await submit({
        maintenanceInfo,
        locationName: location,
        categoryName: category,
        subCategoryName: subCategory,
        problemName: problem,
        severityName: severity,
        description,
        phoneNumber,
        permissionChoice,
        attachments: files as any,
      });
      showSuccess('Service request submitted successfully');
      onClose();
      setDescription('');
      setLocation('');
      setCategory('');
      setSubCategory('');
      setProblem('');
      setSeverity('');
      setPhoneNumber('');
      setPermissionChoice(null);
      setFiles([] as any);
    } catch (e) {
      showError('Failed to submit service request');
    }
  };

  // Derived flags to preserve previous UX (show required on presence of options)
  const isLocationRequired = (locationOptions.length ?? 0) > 0;
  const isProblemRequired = (problemOptions.length ?? 0) > 0;
  const isCategoryRequired = (categoryOptions.length ?? 0) > 0;
  const isSubCategoryRequired = (subCategoryOptions.length ?? 0) > 0;
  const permissionToEnter = permissionChoice === 'yes';

  const modal = (
    <View style={styles.overlay}>
      <View style={styles.modal}>
        <View style={styles.header}>
          <Text style={styles.title}>Request a service</Text>
          <Pressable style={styles.close} onPress={onClose}>
            <Text style={styles.closeText}>✕</Text>
          </Pressable>
        </View>
        <ScrollView style={styles.content} contentContainerStyle={styles.contentInner}>
          <Text style={styles.heading}>We're happy to help, please tell us more</Text>

          {!!locationOptions.length && (
            <View style={styles.fieldWrapper}>
              <DesktopDropdownSelector
                label="Where is the problem?"
                placeholder="Where is the problem?"
                required={isLocationRequired}
                selectedValue={location}
                options={locationOptions.map<DesktopDropdownOption>((l) => ({ label: l, value: l }))}
                onChange={(val) => { setLocation(val); setLocationTouched(true); }}
                error={isLocationRequired && !location && locationTouched ? 'Required' : ''}
              />
            </View>
          )}

          {!!problemOptions.length && (
            <View style={styles.fieldWrapper}>
              <DesktopDropdownSelector
                label="What's happening?"
                placeholder="What's happening?"
                required={isProblemRequired}
                selectedValue={problem}
                options={problemOptions.map<DesktopDropdownOption>((p) => ({ label: p, value: p }))}
                onChange={(val) => { setProblem(val); setProblemTouched(true); }}
                error={isProblemRequired && !problem && problemTouched ? 'Required' : ''}
              />
            </View>
          )}

          {!!categoryOptions.length && (
            <View style={styles.fieldWrapper}>
              <DesktopDropdownSelector
                label="What type of problem is this?"
                placeholder="What type of problem is this?"
                required={isCategoryRequired}
                selectedValue={category}
                options={categoryOptions.map<DesktopDropdownOption>((c) => ({ label: c, value: c }))}
                onChange={(val) => {
                  setCategory(val);
                  setSubCategory('');
                  setCategoryTouched(true);
                }}
                error={isCategoryRequired && !category && categoryTouched ? 'Required' : ''}
              />
            </View>
          )}

          {!!subCategoryOptions.length && (
            <View style={styles.fieldWrapper}>
              <DesktopDropdownSelector
                label="What's having issues?"
                placeholder="What's having issues?"
                required={isSubCategoryRequired}
                selectedValue={subCategory}
                options={subCategoryOptions.map<DesktopDropdownOption>((s) => ({ label: s, value: s }))}
                onChange={(val) => { setSubCategory(val); setSubCategoryTouched(true); }}
                error={isSubCategoryRequired && !subCategory && subCategoryTouched ? 'Required' : ''}
              />
            </View>
          )}

          <View style={styles.fieldWrapper}>
            <DesktopDropdownSelector
              label="How serious is the problem?"
              placeholder="How serious is the problem?"
              required
              selectedValue={severity}
              options={[...severityOptions].sort(sortMaintenanceSeverityNames).map<DesktopDropdownOption>((s) => ({ label: s, value: s }))}
              onChange={(val) => { setSeverity(val); setSeverityTouched(true); }}
              error={!severity && severityTouched ? 'Required' : ''}
            />
          </View>

          <Text style={styles.label}>Description</Text>
          <TextInput
            style={[styles.input, styles.textarea]}
            placeholder="Description"
            multiline
            numberOfLines={6}
            value={description}
            onChangeText={setDescription}
          />

          <Text style={styles.label}>Provide a phone number for updates to your ticket</Text>
          <TextInput
            style={styles.input}
            placeholder="Phone Number"
            keyboardType="phone-pad"
            value={phoneNumber}
            onChangeText={(t) => { setPhoneNumber(normalizePhoneInput(t)); }}
            onBlur={() => setPhoneTouched(true)}
          />
          {phoneTouched && !phoneNumberRegex.test(phoneNumber || '') ? (
            <Text style={styles.errorText}>Enter a valid phone number</Text>
          ) : null}

          {/* Attachments (web/desktop) */}
          <View style={{ marginTop: 12 }}>
            <FileUploadSection
              title={'Attachments'}
              subtitle={'Add photos or files (optional)'}
              files={files}
              onFilesChange={handleFilesChange}
            />
          </View>

          <View style={styles.checkboxRow}>
            <Pressable
              accessibilityRole="checkbox"
              accessibilityState={{ checked: permissionToEnter }}
              onPress={() => setPermissionChoice(permissionChoice === 'yes' ? 'no' : 'yes')}
              style={styles.checkboxBox}
            >
              {permissionToEnter ? <View style={styles.checkboxTick} /> : null}
            </Pressable>
            <Text style={styles.checkboxLabel}>
              By checking, you are giving your permission for our technician to enter your unit. If you have any
              questions or concerns, please contact the front desk.
            </Text>
          </View>
        </ScrollView>
        <View style={styles.footer}>
          <RNButton
            title="Cancel"
            variant="outlined"
            onPress={onClose}
            disabled={status.isLoading}
            textStyle={styles.cancelText}
            style={styles.cancel}
          />
          <RNButton
            title={status.isLoading ? 'Submitting...' : 'Submit'}
            variant="primary"
            onPress={handleSubmit}
            disabled={status.isLoading || !isFormValid}
            loading={status.isLoading}
            textStyle={styles.submitText}
          />
        </View>
      </View>
    </View>
  );

  if (!visible) return null;
  // Web-only rendering for modal. Native screens use CreateMaintenanceTicketScreen
  if (Platform.OS !== 'web') return null;

  try {
    const g: any = globalThis as any;
    const ReactDOM = g?.require ? g.require('react-dom') : require('react-dom');
    const target = g?.document?.body;
    if (ReactDOM?.createPortal && target) {
      return ReactDOM.createPortal(modal, target);
    }
  } catch {}

  return modal;
};

const styles = StyleSheet.create({
  overlay: {
    position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    zIndex: 10000,
  },
  modal: {
    width: '100%',
    maxWidth: 560,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER_SECONDARY,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  close: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeText: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT.PRIMARY,
    fontWeight: '700',
  },
  content: {
    ...(Platform.OS === 'web' && ({ maxHeight: '70vh' } as any)),
  },
  contentInner: {
    padding: 20,
    gap: 8,
  },
  fieldWrapper: {
    marginTop: 8,
  },
  heading: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 6,
    fontWeight: '500',
  },
  label: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    marginTop: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  textarea: {
    minHeight: 100,
    textAlignVertical: 'top' as any,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    marginTop: 12,
  },
  checkboxLabel: {
    flex: 1,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    fontSize: 12,
    lineHeight: 18,
  },
  checkboxBox: {
    width: 20,
    height: 20,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  checkboxTick: {
    width: 12,
    height: 12,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.BUTTON_TERTIARY,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER_SECONDARY,
  },
  uploadLabel: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    ...(Platform.OS === 'web' && ({ cursor: 'pointer' } as any)),
  },
  uploadText: {
    color: ADMIN_COLORS.TEXT.PRIMARY,
    fontWeight: '600',
  },
  errorText: {
    color: ADMIN_COLORS.ERROR,
    fontSize: 12,
    marginTop: 4,
  },
  button: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  cancel: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: RESIDENTS_DESKTOP_COLORS.BUTTON_SECONDARY,
  },
  cancelText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontWeight: '700',
  },
  submitText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontWeight: '700',
  },
});

export default CreateMaintenanceTicketModal;


